// frontend/src/components/comments/CommentList.tsx
import React, { useState } from "react";
import { ActionIcon, Avatar, Box, Group, Loader, Stack, Text, Textarea } from "@mantine/core";
import { FaPen, FaTrash, FaTimes, FaCheck } from "react-icons/fa";
import type { IComment } from "@backend/src/shared/schemas/schemas";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetAuthUsersByUserIdProfilePhotoQuery } from "../../../openapi";

export type CommentListProps = {
  comments: IComment[];
  isLoading?: boolean;
  /** Inloggad användares id – används för att styra edit/delete per kommentar */
  currentUserId?: string;
  onEditComment?: (commentId: string, newMessage: string) => void | Promise<void>;
  onDeleteComment?: (commentId: string) => void | Promise<void>;
  formatDate?: (date: string | Date | null | undefined) => string;
};

export const CommentList: React.FC<CommentListProps> = ({
  comments,
  isLoading = false,
  currentUserId,
  onEditComment,
  onDeleteComment,
  formatDate,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  const defaultFormatDate = (value: string | Date | null | undefined): string => {
    if (!value) return "";
    const d = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("nb-NO");
  };

  const fmt = formatDate ?? defaultFormatDate;

  const startEdit = (comment: IComment) => {
    if (!onEditComment) return;
    setEditingId(String(comment._id));
    setEditingValue(comment.message ?? "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const saveEdit = async (commentId: string) => {
    if (!onEditComment) return;
    const trimmed = editingValue.trim();
    if (!trimmed) return;

    try {
      setIsSaving(true);
      await Promise.resolve(onEditComment(commentId, trimmed));
      setEditingId(null);
      setEditingValue("");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!onDeleteComment) return;
    try {
      setIsDeletingId(commentId);
      await Promise.resolve(onDeleteComment(commentId));
    } finally {
      setIsDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <Group align="center" justify="center" py="sm">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Laster kommentarer…
        </Text>
      </Group>
    );
  }

  if (!comments.length) {
    return (
      <Text size="sm" c="dimmed">
        Ingen kommentarer ennå.
      </Text>
    );
  }

  return (
    <Stack gap="xs">
      {comments.map((comment) => {
        const id = String(comment._id);
        const isEditing = editingId === id;

        const createdById = comment.createdBy?._id;
        const canEditThis =
          !!currentUserId && !!createdById && String(createdById) === String(currentUserId);

        return (
          <Box
            key={id}
            style={{
              borderRadius: 6,
              padding: 8,
              border: "1px solid #e0e0e0",
              background: "#fff",
            }}
          >
            <Group justify="space-between" align="flex-start" mb={4} wrap="nowrap">
              <Group gap="xs" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
                <CommentAuthorAvatar
                  userId={createdById ? String(createdById) : undefined}
                  name={comment.createdBy?.name}
                />
                <div style={{ minWidth: 0 }}>
                  <Text size="xs" c="dimmed">
                    {comment.createdBy?.name ?? "Ukjent bruker"}
                    {comment.createdAt && (
                      <>
                        {" · "}
                        {fmt(comment.createdAt)}
                      </>
                    )}
                    {comment.editedAt && (
                      <Text span size="xs" c="dimmed">
                        {" · redigert"}
                      </Text>
                    )}
                  </Text>
                </div>
              </Group>

              {canEditThis && (onEditComment || onDeleteComment) && (
                <Group gap={4}>
                  {onEditComment && (
                    <>
                      {!isEditing ? (
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          aria-label="Rediger kommentar"
                          onClick={() => startEdit(comment)}
                        >
                          <FaPen size={12} />
                        </ActionIcon>
                      ) : (
                        <>
                          <ActionIcon
                            size="xs"
                            variant="light"
                            aria-label="Avbryt redigering"
                            onClick={cancelEdit}
                            disabled={isSaving}
                          >
                            <FaTimes size={12} />
                          </ActionIcon>
                          <ActionIcon
                            size="xs"
                            variant="filled"
                            aria-label="Lagre kommentar"
                            onClick={() => saveEdit(id)}
                            disabled={isSaving}
                          >
                            <FaCheck size={12} />
                          </ActionIcon>
                        </>
                      )}
                    </>
                  )}

                  {onDeleteComment && (
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="red"
                      aria-label="Slett kommentar"
                      onClick={() => handleDelete(id)}
                      disabled={isDeletingId === id}
                    >
                      <FaTrash size={12} />
                    </ActionIcon>
                  )}
                </Group>
              )}
            </Group>

            {!isEditing ? (
              <Text size="sm" pl={34}>
                {comment.message}
              </Text>
            ) : (
              <Textarea
                value={editingValue}
                onChange={(e) => setEditingValue(e.currentTarget.value)}
                autosize
                minRows={2}
                maxRows={6}
                disabled={isSaving}
              />
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

const getInitials = (name: string | null | undefined): string => {
  const parts = (name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return "?";

  return parts
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const CommentAuthorAvatar: React.FC<{ userId?: string; name?: string }> = ({ userId, name }) => {
  const { data } = useGetAuthUsersByUserIdProfilePhotoQuery(userId ? { userId } : skipToken);

  return (
    <Avatar src={data?.dataUrl ?? undefined} size={26} radius="xl" color="blue">
      {getInitials(name)}
    </Avatar>
  );
};
